/**
 * Hook for single proposal detail operations
 */

import {
  keepPreviousData,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { logger } from '@/lib/logger';

import type { ProposalAPIError } from '../core/errors';
import type { ProposalDetail } from '../core/types';
import type { proposalQueryKeys } from '../query/keys';
import { proposalDetailQuery } from '../query/queries';
import { propagateProposalReadAt } from '../realtime';

/**
 * Propagates the freshly-fetched read_at from the detail cache into every
 * matching list cache for the org. Used by useProposal AND useProposalOptional
 * so both paths stay in sync. The id-guard handles useProposalOptional's
 * keepPreviousData (data may briefly belong to a previous proposalId during
 * transitions).
 */
function useReadAtPropagation(
  orgId: number,
  proposalId: number,
  data: ProposalDetail | undefined
): void {
  const queryClient = useQueryClient();
  const dataId = data?.id;
  // Coerce undefined → null so the dep array has a stable primitive type.
  const dataReadAt = data?.read_at ?? null;
  useEffect(() => {
    if (dataId === undefined || dataId !== proposalId) {
      // Either no data yet, or keepPreviousData transition — incoming data is
      // for a different proposalId; do not propagate its read_at into the
      // wrong list row.
      return;
    }
    if (dataReadAt === null) {
      return;
    }
    propagateProposalReadAt(queryClient, orgId, proposalId, dataReadAt);
  }, [orgId, proposalId, dataId, dataReadAt, queryClient]);
}

/**
 * Hook for getting a single proposal with full decrypted details
 *
 * @param orgId - Organization ID
 * @param proposalId - Proposal ID
 * @param options - Additional query options
 * @returns Query result with proposal detail
 *
 * @example
 * ```typescript
 * const { data: proposal, isLoading } = useProposal(orgId, proposalId);
 *
 * if (isLoading) return <div>Loading...</div>;
 *
 * return (
 *   <div>
 *     <h1>{proposal.title}</h1>
 *     <p>Status: {proposal.resolution}</p>
 *     <div>
 *       {proposal.actions.map(action => (
 *         <div key={action.id}>
 *           {action.proposed_action_type_name}
 *         </div>
 *       ))}
 *     </div>
 *   </div>
 * );
 * ```
 */
export function useProposal(
  orgId: number,
  proposalId: number,
  options?: Partial<
    Omit<
      UseQueryOptions<
        ProposalDetail,
        ProposalAPIError,
        ProposalDetail,
        ReturnType<typeof proposalQueryKeys.detail>
      >,
      'queryKey' | 'queryFn'
    >
  >
): UseQueryResult<ProposalDetail, ProposalAPIError> {
  const queryOpts = useMemo(
    () => proposalDetailQuery(orgId, proposalId),
    [orgId, proposalId]
  );

  const result = useQuery({
    ...queryOpts,
    ...options,
  });

  useReadAtPropagation(orgId, proposalId, result.data);

  // Log cache subscription activity for development debugging
  // IMPORTANT: Never log result.data.actions (contains email payloads, recipients)
  useEffect(() => {
    try {
      if (result.data) {
        logger.debug('[useProposal] Cache updated', {
          orgId,
          proposalId,
          actionCount: result.data.actions.length,
          readAt: result.data.read_at,
          timestamp: new Date().toISOString(),
        });
      }
    } catch {
      // Silently fail - don't break the hook on logging errors
    }
  }, [result.data, orgId, proposalId]);

  return result;
}

/**
 * Hook for conditionally fetching a proposal (useful for optional IDs)
 *
 * @param orgId - Organization ID
 * @param proposalId - Proposal ID (undefined to skip query)
 * @param options - Additional query options
 * @returns Query result with proposal detail
 *
 * @example
 * ```typescript
 * // Only fetch if proposalId is defined
 * const { data } = useProposalOptional(orgId, selectedProposalId);
 * ```
 */
export function useProposalOptional(
  orgId: number,
  proposalId: number | undefined,
  options?: Partial<
    Omit<
      UseQueryOptions<
        ProposalDetail,
        ProposalAPIError,
        ProposalDetail,
        ReturnType<typeof proposalQueryKeys.detail>
      >,
      'queryKey' | 'queryFn'
    >
  >
): UseQueryResult<ProposalDetail, ProposalAPIError> {
  const queryOpts = useMemo(
    () => proposalDetailQuery(orgId, proposalId ?? 0),
    [orgId, proposalId]
  );
  const queryResult = useQuery({
    ...queryOpts,
    enabled: proposalId !== undefined,
    // Keep previous data during transitions to prevent layout reflow
    placeholderData: keepPreviousData,
    ...(options || {}),
  });

  useReadAtPropagation(orgId, proposalId ?? 0, queryResult.data);

  return queryResult;
}
